import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, ListOrdered, Loader2, Mic, Save, ShieldCheck, Eye } from 'lucide-react';
import api from '../../../../api/client';
import ConfirmationDialog from '../../../common/ConfirmationDialog';

const MIN_MAX_NO_OF_QUESTIONS = 5;
const INTERVIEW_DURATION_DEFAULT = 30;
const INTERVIEW_DURATION_MIN = 15;
const INTERVIEW_DURATION_MAX = 120;

const parseRequireAudioVerification = (value, defaultValue = true) => {
  if (value === false || value === 'false' || value === 0 || value === '0') return false;
  if (value === true || value === 'true' || value === 1 || value === '1') return true;
  return defaultValue;
};

function ToggleSwitch({ checked, onClick, disabled, id }) {
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={onClick}
      className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 ${
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
      } ${checked ? 'bg-sky-600' : 'bg-gray-200'}`}
    >
      <span
        className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
          checked ? 'translate-x-4' : 'translate-x-0'
        }`}
      />
    </button>
  );
}

function SettingToggleRow({ icon, title, help, checked, onClick, disabled, statusOn, statusOff }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-50 text-slate-500">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-gray-900">{title}</p>
        <p className="mt-0.5 text-xs leading-snug text-gray-500">{help}</p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <span className="hidden text-xs text-gray-500 sm:inline">
          {checked ? statusOn : statusOff}
        </span>
        <ToggleSwitch checked={checked} onClick={onClick} disabled={disabled} />
      </div>
    </div>
  );
}

export default function InterviewConfigurationPanel({
  selectedCompany,
  companies,
  setCompanies,
  loading,
  onNotify,
  onVerificationRequiredChange,
  onShowingIdentityChange,
}) {
  const { t } = useTranslation();
  const [timePerQuestionInput, setTimePerQuestionInput] = useState('');
  const [interviewDurationInput, setInterviewDurationInput] = useState(String(INTERVIEW_DURATION_DEFAULT));
  const [requireAudioVerification, setRequireAudioVerification] = useState(true);
  const [interviewConfigErrors, setInterviewConfigErrors] = useState({});
  const [isInterviewConfigSaving, setIsInterviewConfigSaving] = useState(false);
  const [updatingVerification, setUpdatingVerification] = useState(false);
  const [updatingShowingIdentity, setUpdatingShowingIdentity] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [pendingVerificationValue, setPendingVerificationValue] = useState(null);

  const activeCompany = companies.find((company) => company.id === selectedCompany);
  const verificationRequired = activeCompany?.verification_required ?? false;
  const showingIdentity = activeCompany?.showing_identity ?? false;

  useEffect(() => {
    const interviewSettings = activeCompany?.settings?.interview_settings || {};
    const maxNoOfQuestions = interviewSettings.max_no_of_questions;
    const interviewDurationMinutes = interviewSettings.interview_duration_minutes;

    setTimePerQuestionInput(
      maxNoOfQuestions == null || maxNoOfQuestions === ''
        ? ''
        : String(maxNoOfQuestions)
    );
    setInterviewDurationInput(
      interviewDurationMinutes == null || interviewDurationMinutes === ''
        ? String(INTERVIEW_DURATION_DEFAULT)
        : String(interviewDurationMinutes)
    );
    setRequireAudioVerification(parseRequireAudioVerification(interviewSettings.require_audio_verification, true));
    setInterviewConfigErrors({});
  }, [activeCompany]);

  const validateInterviewConfiguration = () => {
    const nextErrors = {};
    const trimmedTimePerQuestion = timePerQuestionInput.trim();
    const trimmedInterviewDuration = interviewDurationInput.trim();

    if (trimmedTimePerQuestion !== '') {
      const parsedTimePerQuestion = Number(trimmedTimePerQuestion);
      if (!Number.isFinite(parsedTimePerQuestion) || !Number.isInteger(parsedTimePerQuestion)) {
        nextErrors.timePerQuestion = t('companyAdmins.interviewConfig.timePerQuestionIntegerError', 'Please enter a whole number of questions.');
      } else if (parsedTimePerQuestion < MIN_MAX_NO_OF_QUESTIONS) {
        nextErrors.timePerQuestion = t(
          'companyAdmins.interviewConfig.timePerQuestionMinError',
          'Number of questions must be at least {{min}}.',
          { min: MIN_MAX_NO_OF_QUESTIONS }
        );
      }
    }

    if (trimmedInterviewDuration === '') {
      nextErrors.interviewDuration = t('companyAdmins.interviewConfig.interviewDurationRequired', 'Please enter a whole number of minutes.');
    } else {
      const parsedInterviewDuration = Number(trimmedInterviewDuration);
      if (!Number.isFinite(parsedInterviewDuration) || !Number.isInteger(parsedInterviewDuration) || parsedInterviewDuration <= 0) {
        nextErrors.interviewDuration = t('companyAdmins.interviewConfig.interviewDurationIntegerError', 'Please enter a whole number of minutes.');
      } else if (parsedInterviewDuration < INTERVIEW_DURATION_MIN || parsedInterviewDuration > INTERVIEW_DURATION_MAX) {
        nextErrors.interviewDuration = t(
          'companyAdmins.interviewConfig.interviewDurationRangeError',
          'Interview duration must be between {{min}} and {{max}} minutes.',
          { min: INTERVIEW_DURATION_MIN, max: INTERVIEW_DURATION_MAX }
        );
      }
    }

    return nextErrors;
  };

  const handleSaveInterviewConfiguration = async () => {
    if (!selectedCompany) return;

    const nextErrors = validateInterviewConfiguration();
    setInterviewConfigErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) return;

    const existingSettings = activeCompany?.settings || {};
    const existingInterviewSettings = existingSettings.interview_settings || {};
    const trimmedTimePerQuestion = timePerQuestionInput.trim();
    const trimmedInterviewDuration = interviewDurationInput.trim();

    const payload = {
      settings: {
        ...existingSettings,
        interview_settings: {
          ...existingInterviewSettings,
          max_no_of_questions: trimmedTimePerQuestion === '' ? null : Number(trimmedTimePerQuestion),
          interview_duration_minutes: Number(trimmedInterviewDuration),
          require_audio_verification: requireAudioVerification
        }
      }
    };

    try {
      setIsInterviewConfigSaving(true);
      const { data } = await api.patch(`/companies/${selectedCompany}/`, payload);
      setCompanies((prevCompanies) =>
        prevCompanies.map((company) => (
          company.id === selectedCompany
            ? { ...company, settings: data.settings || payload.settings }
            : company
        ))
      );
      onNotify?.({
        open: true,
        message: t('companyAdmins.interviewConfig.saveSuccess', 'Interview configuration updated successfully.'),
        severity: 'success'
      });
      window.dispatchEvent(new CustomEvent('company-data-updated'));
    } catch (err) {
      console.error('Error updating interview configuration:', err);
      onNotify?.({
        open: true,
        message: err.response?.data?.error || err.response?.data?.detail || t('companyAdmins.interviewConfig.saveError', 'Failed to update interview configuration.'),
        severity: 'error'
      });
    } finally {
      setIsInterviewConfigSaving(false);
    }
  };

  const handleVerificationToggleClick = () => {
    if (!selectedCompany || !onVerificationRequiredChange) return;
    setPendingVerificationValue(!verificationRequired);
    setConfirmModalOpen(true);
  };

  const handleConfirmVerificationChange = async () => {
    if (!selectedCompany || !onVerificationRequiredChange || pendingVerificationValue === null) return;
    setConfirmModalOpen(false);
    setUpdatingVerification(true);
    try {
      await onVerificationRequiredChange(selectedCompany, pendingVerificationValue);
      setPendingVerificationValue(null);
    } finally {
      setUpdatingVerification(false);
    }
  };

  const handleShowingIdentityToggleClick = async () => {
    if (!selectedCompany || !onShowingIdentityChange || !verificationRequired) return;
    setUpdatingShowingIdentity(true);
    try {
      await onShowingIdentityChange(selectedCompany, !showingIdentity);
    } finally {
      setUpdatingShowingIdentity(false);
    }
  };

  const isUnlimited = timePerQuestionInput.trim() === '';
  const durationNum = Number(interviewDurationInput);
  const durationDisplay = Number.isFinite(durationNum) ? durationNum : INTERVIEW_DURATION_DEFAULT;

  const questionsSummary = isUnlimited
    ? t('companyAdmins.interviewConfig.summaryUnlimitedQuestions', 'Unlimited questions')
    : t('companyAdmins.interviewConfig.summaryQuestions', '{{count}} questions', { count: Number(timePerQuestionInput) });
  const durationSummary = t('companyAdmins.interviewConfig.summaryMinutes', '{{count}} min', { count: durationDisplay });
  const audioSummary = requireAudioVerification
    ? t('companyAdmins.interviewConfig.summaryAudioOn', 'Audio verification on')
    : t('companyAdmins.interviewConfig.summaryAudioOff', 'Audio verification off');
  const identitySummary = verificationRequired
    ? t('companyAdmins.interviewConfig.summaryIdentityOn', 'Identity check on')
    : t('companyAdmins.interviewConfig.summaryIdentityOff', 'Identity check off');

  const inputShellClass = (hasError) =>
    `flex h-9 rounded-lg border bg-white transition-colors ${
      hasError
        ? 'border-red-400 focus-within:border-red-500 focus-within:ring-2 focus-within:ring-red-500/20'
        : 'border-gray-200 focus-within:border-sky-400 focus-within:ring-2 focus-within:ring-sky-500/20'
    }`;

  if (!selectedCompany || loading) return null;

  return (
    <div className="p-4 sm:p-5">
      <div className="space-y-4">
        <div
          className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-sky-100 bg-sky-50/70 px-3 py-2 text-xs"
          role="status"
        >
          <span className="font-semibold text-sky-900">
            {t('companyAdmins.interviewConfig.previewLabel', 'Default preview')}
          </span>
          <span className="text-sky-300" aria-hidden>·</span>
          <span className="text-sky-800">{questionsSummary}</span>
          <span className="text-sky-300" aria-hidden>·</span>
          <span className="text-sky-800">{durationSummary}</span>
          <span className="text-sky-300" aria-hidden>·</span>
          <span className="text-sky-800">{audioSummary}</span>
          <span className="text-sky-300" aria-hidden>·</span>
          <span className="text-sky-800">{identitySummary}</span>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          <div className="grid sm:grid-cols-2 sm:divide-x divide-y sm:divide-y-0 divide-gray-100">
            <div className="p-4">
              <div className="flex items-center gap-2">
                <ListOrdered size={16} className="text-sky-600" aria-hidden />
                <h4 className="text-sm font-semibold text-gray-900">
                  {t('companyAdmins.interviewConfig.numberOfQuestions', 'Number of questions')}
                </h4>
              </div>
              <p className="mt-1 text-xs leading-snug text-gray-500">
                {t(
                  'companyAdmins.interviewConfig.numberOfQuestionsHelpShort',
                  'Leave empty for unlimited. Minimum {{min}} when set.',
                  { min: MIN_MAX_NO_OF_QUESTIONS }
                )}
              </p>
              <div className="mt-2.5 flex items-center gap-2">
                <label htmlFor="interview-max-questions" className="sr-only">
                  {t('companyAdmins.interviewConfig.numberOfQuestions', 'Number of questions')}
                </label>
                <div className={`${inputShellClass(!!interviewConfigErrors.timePerQuestion)} min-w-0 flex-1 max-w-[200px]`}>
                  <input
                    id="interview-max-questions"
                    type="number"
                    min={MIN_MAX_NO_OF_QUESTIONS}
                    step={1}
                    value={timePerQuestionInput}
                    onKeyDown={(e) => {
                      if (['-', '+', 'e', 'E', '.', ','].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    onPaste={(e) => {
                      const pasted = (e.clipboardData || window.clipboardData)?.getData('text') || '';
                      if (!/^\d+$/.test(pasted.trim())) {
                        e.preventDefault();
                      }
                    }}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '') {
                        setTimePerQuestionInput('');
                        if (interviewConfigErrors.timePerQuestion) {
                          setInterviewConfigErrors((prev) => ({ ...prev, timePerQuestion: null }));
                        }
                        return;
                      }
                      const digitsOnly = val.replace(/\D/g, '');
                      setTimePerQuestionInput(digitsOnly);
                      if (digitsOnly !== '' && Number(digitsOnly) < MIN_MAX_NO_OF_QUESTIONS) {
                        setInterviewConfigErrors((prev) => ({
                          ...prev,
                          timePerQuestion: t(
                            'companyAdmins.interviewConfig.timePerQuestionMinError',
                            'Number of questions must be at least {{min}}.',
                            { min: MIN_MAX_NO_OF_QUESTIONS }
                          )
                        }));
                      } else if (interviewConfigErrors.timePerQuestion) {
                        setInterviewConfigErrors((prev) => ({ ...prev, timePerQuestion: null }));
                      }
                    }}
                    onBlur={() => {
                      if (timePerQuestionInput !== '' && Number(timePerQuestionInput) < MIN_MAX_NO_OF_QUESTIONS) {
                        setInterviewConfigErrors((prev) => ({
                          ...prev,
                          timePerQuestion: t(
                            'companyAdmins.interviewConfig.timePerQuestionMinError',
                            'Number of questions must be at least {{min}}.',
                            { min: MIN_MAX_NO_OF_QUESTIONS }
                          )
                        }));
                      }
                    }}
                    placeholder={t('companyAdmins.interviewConfig.unlimitedPlaceholder', 'Unlimited')}
                    className="min-w-0 flex-1 border-0 bg-transparent px-3 py-1.5 text-sm font-medium text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0"
                  />
                  <span className="flex shrink-0 items-center pr-2.5 text-[11px] font-medium text-gray-400">
                    {t('companyAdmins.interviewConfig.questionsSuffix', 'questions')}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setTimePerQuestionInput('');
                    if (interviewConfigErrors.timePerQuestion) {
                      setInterviewConfigErrors((prev) => ({ ...prev, timePerQuestion: null }));
                    }
                  }}
                  className="shrink-0 rounded-md px-2 py-1 text-xs font-medium text-sky-700 hover:bg-sky-50"
                >
                  {t('companyAdmins.interviewConfig.useUnlimited', 'Use unlimited')}
                </button>
              </div>
              {interviewConfigErrors.timePerQuestion && (
                <p className="mt-1.5 text-xs text-red-600">{interviewConfigErrors.timePerQuestion}</p>
              )}
            </div>

            <div className="p-4">
              <div className="flex items-center gap-2">
                <Clock size={16} className="text-fuchsia-600" aria-hidden />
                <h4 className="text-sm font-semibold text-gray-900">
                  {t('companyAdmins.interviewConfig.interviewDuration', 'Interview duration')}
                </h4>
              </div>
              <p className="mt-1 text-xs leading-snug text-gray-500">
                {t(
                  'companyAdmins.interviewConfig.interviewDurationHelpShort',
                  'Allowed {{min}}–{{max}} minutes. Default {{defaultValue}}.',
                  {
                    defaultValue: INTERVIEW_DURATION_DEFAULT,
                    min: INTERVIEW_DURATION_MIN,
                    max: INTERVIEW_DURATION_MAX
                  }
                )}
              </p>
              <div className="mt-2.5">
                <label htmlFor="interview-duration-mins" className="sr-only">
                  {t('companyAdmins.interviewConfig.interviewDuration', 'Interview duration')}
                </label>
                <div className={`${inputShellClass(!!interviewConfigErrors.interviewDuration)} max-w-[200px]`}>
                  <input
                    id="interview-duration-mins"
                    type="number"
                    min={INTERVIEW_DURATION_MIN}
                    max={INTERVIEW_DURATION_MAX}
                    step={1}
                    value={interviewDurationInput}
                    onKeyDown={(e) => {
                      if (['-', '+', 'e', 'E', '.', ','].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    onPaste={(e) => {
                      const pasted = (e.clipboardData || window.clipboardData)?.getData('text') || '';
                      if (!/^\d+$/.test(pasted.trim())) {
                        e.preventDefault();
                        return;
                      }
                      const num = parseInt(pasted.trim(), 10);
                      if (num > INTERVIEW_DURATION_MAX) {
                        e.preventDefault();
                        setInterviewDurationInput(String(INTERVIEW_DURATION_MAX));
                        if (interviewConfigErrors.interviewDuration) {
                          setInterviewConfigErrors((prev) => ({ ...prev, interviewDuration: null }));
                        }
                      }
                    }}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '') {
                        setInterviewDurationInput('');
                        if (interviewConfigErrors.interviewDuration) {
                          setInterviewConfigErrors((prev) => ({ ...prev, interviewDuration: null }));
                        }
                        return;
                      }
                      const digitsOnly = val.replace(/\D/g, '');
                      if (digitsOnly === '') {
                        setInterviewDurationInput('');
                        if (interviewConfigErrors.interviewDuration) {
                          setInterviewConfigErrors((prev) => ({ ...prev, interviewDuration: null }));
                        }
                        return;
                      }
                      const num = parseInt(digitsOnly, 10);
                      if (num > INTERVIEW_DURATION_MAX) {
                        setInterviewDurationInput(String(INTERVIEW_DURATION_MAX));
                      } else {
                        setInterviewDurationInput(digitsOnly);
                      }
                      if (num < INTERVIEW_DURATION_MIN || num > INTERVIEW_DURATION_MAX) {
                        setInterviewConfigErrors((prev) => ({
                          ...prev,
                          interviewDuration: t(
                            'companyAdmins.interviewConfig.interviewDurationRangeError',
                            'Interview duration must be between {{min}} and {{max}} minutes.',
                            { min: INTERVIEW_DURATION_MIN, max: INTERVIEW_DURATION_MAX }
                          )
                        }));
                      } else if (interviewConfigErrors.interviewDuration) {
                        setInterviewConfigErrors((prev) => ({ ...prev, interviewDuration: null }));
                      }
                    }}
                    onBlur={() => {
                      if (interviewDurationInput !== '') {
                        const num = parseInt(interviewDurationInput, 10);
                        if (num > INTERVIEW_DURATION_MAX) {
                          setInterviewDurationInput(String(INTERVIEW_DURATION_MAX));
                        } else if (num < INTERVIEW_DURATION_MIN) {
                          setInterviewConfigErrors((prev) => ({
                            ...prev,
                            interviewDuration: t(
                              'companyAdmins.interviewConfig.interviewDurationRangeError',
                              'Interview duration must be between {{min}} and {{max}} minutes.',
                              { min: INTERVIEW_DURATION_MIN, max: INTERVIEW_DURATION_MAX }
                            )
                          }));
                        }
                      }
                    }}
                    className="min-w-0 flex-1 border-0 bg-transparent px-3 py-1.5 text-sm font-medium text-gray-900 focus:outline-none focus:ring-0"
                  />
                  <span className="flex items-center pr-2.5 text-[11px] font-medium text-gray-400">
                    {t('companyAdmins.interviewConfig.minutesSuffix', 'min')}
                  </span>
                </div>
              </div>
              {interviewConfigErrors.interviewDuration && (
                <p className="mt-1.5 text-xs text-red-600">{interviewConfigErrors.interviewDuration}</p>
              )}
            </div>
          </div>

          <div className="divide-y divide-gray-100 border-t border-gray-100">
            <SettingToggleRow
              icon={<Mic size={15} />}
              title={t('companyAdmins.interviewConfig.requireAudioVerification', 'Require audio verification')}
              help={t(
                'companyAdmins.interviewConfig.requireAudioVerificationHelpShort',
                'Candidates record a spoken sentence to confirm their microphone. Recruiters can override this per invite.'
              )}
              checked={requireAudioVerification}
              onClick={() => setRequireAudioVerification((prev) => !prev)}
              statusOn={t('companyAdmins.interviewConfig.audioVerificationOn', 'Required')}
              statusOff={t('companyAdmins.interviewConfig.audioVerificationOff', 'Optional')}
            />
            {onVerificationRequiredChange && (
              <SettingToggleRow
                icon={<ShieldCheck size={15} />}
                title={t('companyAdmins.verificationRequiredTitle')}
                help={t(
                  'companyAdmins.verificationRequiredHelpShort',
                  'Require candidates to verify identity with a valid ID during the interview.'
                )}
                checked={verificationRequired}
                onClick={handleVerificationToggleClick}
                disabled={updatingVerification}
                statusOn={t('companyAdmins.verificationRequiredOn')}
                statusOff={t('companyAdmins.verificationRequiredOff')}
              />
            )}
            {onShowingIdentityChange && (
              <SettingToggleRow
                icon={<Eye size={15} />}
                title={t('companyAdmins.showingIdentityTitle')}
                help={
                  verificationRequired
                    ? t(
                      'companyAdmins.showingIdentityHelpShort',
                      'Show the candidate’s name and ID on feedback reports and PDFs.'
                    )
                    : t('companyAdmins.showingIdentityDisabledHint')
                }
                checked={showingIdentity}
                onClick={handleShowingIdentityToggleClick}
                disabled={updatingShowingIdentity || !verificationRequired}
                statusOn={t('companyAdmins.showingIdentityOn', 'Visible')}
                statusOff={t('companyAdmins.showingIdentityOff', 'Hidden')}
              />
            )}
          </div>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="max-w-md text-xs leading-relaxed text-gray-500">
            {t(
              'companyAdmins.interviewConfig.saveHint',
              'These defaults are stored on the server and apply to this company going forward.'
            )}
          </p>
          <button
            type="button"
            onClick={handleSaveInterviewConfiguration}
            disabled={isInterviewConfigSaving}
            className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all duration-200 ${
              isInterviewConfigSaving ? 'cursor-not-allowed bg-gray-400 shadow-none' : 'hover:opacity-95'
            }`}
            style={{
              background: isInterviewConfigSaving ? undefined : 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)'
            }}
          >
            {isInterviewConfigSaving ? (
              <>
                <Loader2 size={16} className="animate-spin" aria-hidden />
                {t('companyAdmins.interviewConfig.saving', 'Saving...')}
              </>
            ) : (
              <>
                <Save size={16} aria-hidden />
                {t('companyAdmins.interviewConfig.save', 'Save configuration')}
              </>
            )}
          </button>
        </div>
      </div>

      <ConfirmationDialog
        open={confirmModalOpen}
        onClose={() => {
          setConfirmModalOpen(false);
          setPendingVerificationValue(null);
        }}
        onConfirm={handleConfirmVerificationChange}
        title={t('companyAdmins.verificationRequiredConfirmTitle')}
        message={
          activeCompany?.name === 'Practice Company'
            ? t('companyAdmins.verificationRequiredConfirmMessageB2C', 'Do you want to change the verification required setting for Practice Interviews (B2C)?')
            : t('companyAdmins.verificationRequiredConfirmMessage')
        }
      />
    </div>
  );
}
